'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Calculator, Zap, Sparkles, ArrowRight, CheckCircle2, ShieldCheck, Flame, Wind, Sun } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';

export function EnergySavingsCalculator() {
  const { format } = useCurrency();
  const [area, setArea] = useState<number>(50);
  const [systemType, setSystemType] = useState<'ac' | 'heatpump' | 'solar'>('ac');
  const [insulation, setInsulation] = useState<'standard' | 'high'>('high');

  // Calculations
  const btuNeeded = Math.round((area * (insulation === 'high' ? 350 : 450)) / 1000) * 1000;
  const kwHeatPump = (area * 0.08 * (insulation === 'high' ? 0.85 : 1.15)).toFixed(1);

  const getSavingsEstimate = () => {
    if (systemType === 'heatpump') {
      const estimatedSaving = Math.round(area * 780); // ALL saved per season vs electric heaters
      return {
        savingAll: estimatedSaving,
        efficiencyPct: '65%',
        recommendation: `Pompë Nxehtësie ${kwHeatPump} kW (Ajër-Ujë A+++)`,
        link: '/category/pompa-nxehtesie',
      };
    }
    if (systemType === 'solar') {
      const estimatedSaving = Math.round(area * 420);
      return {
        savingAll: estimatedSaving,
        efficiencyPct: '75%',
        recommendation: 'Sistem Diellor me Qarkullim të Detyruar & Bojler 200-300L',
        link: '/category/energji-diellore',
      };
    }
    // ac
    const estimatedSaving = Math.round(area * 520);
    const btuTag = btuNeeded <= 10000 ? '9,000 BTU' : btuNeeded <= 14000 ? '12,000 BTU' : btuNeeded <= 20000 ? '18,000 BTU' : '24,000 BTU';
    return {
      savingAll: estimatedSaving,
      efficiencyPct: '45%',
      recommendation: `Kondicioner Inverter ${btuTag} A+++`,
      link: '/category/kondicionere',
    };
  };

  const estimate = getSavingsEstimate();

  return (
    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/90 dark:border-slate-800 shadow-xl overflow-hidden">
      <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-600 p-6 sm:p-8 text-white">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-bold mb-3 backdrop-blur-sm">
          <Calculator className="w-4 h-4 text-sky-200" />
          <span>Llogaritësi Inteligjent Termik</span>
        </div>
        <h3 className="text-xl sm:text-2xl font-black">
          Llogaritni Kapacitetin dhe Kursimin e Energjisë
        </h3>
        <p className="text-xs sm:text-sm text-sky-100 mt-1 max-w-xl">
          Zgjidhni sipërfaqen e ambientit dhe mësoni menjëherë fuqinë e rekomanduar dhe vlerësimin e kursimit në faturën mujore.
        </p>
      </div>

      <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        {/* Controls */}
        <div className="lg:col-span-7 space-y-6">
          {/* System Type Selector */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-2">
              1. Lloji i Zgjidhjes së Dëshiruar:
            </label>
            <div className="grid grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={() => setSystemType('ac')}
                className={`p-3 rounded-2xl border text-left transition-all ${
                  systemType === 'ac'
                    ? 'border-sky-500 bg-sky-50/80 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 shadow-sm'
                    : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-300'
                }`}
              >
                <Wind className="w-5 h-5 text-sky-500 mb-1.5" />
                <div className="font-bold text-xs">Kondicioner</div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400">Inverter A+++</div>
              </button>

              <button
                type="button"
                onClick={() => setSystemType('heatpump')}
                className={`p-3 rounded-2xl border text-left transition-all ${
                  systemType === 'heatpump'
                    ? 'border-rose-500 bg-rose-50/80 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 shadow-sm'
                    : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-300'
                }`}
              >
                <Flame className="w-5 h-5 text-rose-500 mb-1.5" />
                <div className="font-bold text-xs">Pompë Nxehtësie</div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400">Ngrohje & Ftohje</div>
              </button>

              <button
                type="button"
                onClick={() => setSystemType('solar')}
                className={`p-3 rounded-2xl border text-left transition-all ${
                  systemType === 'solar'
                    ? 'border-amber-500 bg-amber-50/80 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 shadow-sm'
                    : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-300'
                }`}
              >
                <Sun className="w-5 h-5 text-amber-500 mb-1.5" />
                <div className="font-bold text-xs">Energji Diellore</div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400">Ujë Sanitar</div>
              </button>
            </div>
          </div>

          {/* Area Slider */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="area-slider" className="text-xs font-bold text-slate-700 dark:text-slate-300">
                2. Sipërfaqja e Ambientit:
              </label>
              <span className="text-base font-black text-sky-600 dark:text-sky-400">
                {area} m²
              </span>
            </div>
            <input
              id="area-slider"
              aria-label="Sipërfaqja e Ambientit"
              type="range"
              min="15"
              max="250"
              step="5"
              value={area}
              onChange={(e) => setArea(Number(e.target.value))}
              className="w-full h-2.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-sky-600"
            />
            <div className="flex justify-between text-[11px] text-slate-400 mt-1">
              <span>15 m² (Dhomë)</span>
              <span>80 m² (Apartament)</span>
              <span>250 m² (Vilë / Biznes)</span>
            </div>
          </div>

          {/* Insulation Toggle */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-2">
              3. Niveli i Izolimit Termik të Ndërtesës:
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setInsulation('high')}
                className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${
                  insulation === 'high'
                    ? 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-500 text-emerald-700 dark:text-emerald-300'
                    : 'border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                ✓ Izolim i Mirë (Dritare dopio xham & kapotë)
              </button>
              <button
                type="button"
                onClick={() => setInsulation('standard')}
                className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${
                  insulation === 'standard'
                    ? 'bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-600 text-slate-900 dark:text-white'
                    : 'border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                Standard / Mesatar
              </button>
            </div>
          </div>
        </div>

        {/* Results Card */}
        <div className="lg:col-span-5 bg-gradient-to-br from-slate-50 to-sky-50/60 dark:from-slate-800/80 dark:to-slate-900/90 rounded-2xl p-6 border border-sky-100 dark:border-slate-700/80 space-y-5">
          <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-700">
            <span className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Rekomandimi Inxhinierik
            </span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 text-[10px] font-black border border-emerald-200 dark:border-emerald-800">
              Efiçiencë A+++
            </span>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-slate-500 dark:text-slate-400">Pajisja e Sugjeruar:</div>
            <div className="text-base sm:text-lg font-black text-slate-900 dark:text-white leading-tight">
              {estimate.recommendation}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700">
              <div className="text-[10px] text-slate-400 font-medium">Ulje e Konsumit:</div>
              <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <Zap className="w-4 h-4 fill-current" />
                {estimate.efficiencyPct}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700">
              <div className="text-[10px] text-slate-400 font-medium">Kursim i Parashikuar:</div>
              <div className="text-lg font-black text-sky-600 dark:text-sky-400">
                ~{format(estimate.savingAll)} <span className="text-[10px] font-normal text-slate-500">/vit</span>
              </div>
            </div>
          </div>

          <div className="space-y-2 text-[11px] text-slate-600 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              <span>Përfshin instalim me vakumim profesional dhe gaz ekologjik R32.</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
              <span>Garanci zyrtare deri në 5 vite nga DYRMA Climate.</span>
            </div>
          </div>

          <div className="pt-2">
            <Link
              href={estimate.link}
              className="w-full py-3 px-4 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-md shadow-sky-600/20 transition-all hover:translate-y-[-1px]"
            >
              Shiko Pajisjet e Rekomanduara
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
